import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from 'contexts/BookingContext';
import { useAuth } from 'contexts/AuthContext';
import { getConfig } from 'services/mewsService';
import { parseEmail } from 'services/emailService';
import { addDays, format } from 'date-fns';
import Toast from 'components/ux/toast/Toast';
import './search.scss';

const HERO_IMAGE =
  'https://kirsebaerkroen.dk/wp-content/uploads/Forside-Slide-01.jpg';

const Search = () => {
  const { state, dispatch } = useBooking();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState(
    state.checkIn ? format(state.checkIn, 'yyyy-MM-dd') : format(addDays(new Date(), 1), 'yyyy-MM-dd')
  );
  const [checkOut, setCheckOut] = useState(
    state.checkOut ? format(state.checkOut, 'yyyy-MM-dd') : format(addDays(new Date(), 3), 'yyyy-MM-dd')
  );
  const [error, setError] = useState('');
  const [configLoading, setConfigLoading] = useState(!state.config);

  // Email parser state
  const [emailText, setEmailText] = useState('');
  const [emailParsing, setEmailParsing] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const loadConfig = useCallback(async () => {
    if (state.config) return;
    try {
      setConfigLoading(true);
      const config = await getConfig();
      dispatch({ type: 'SET_CONFIG', config });
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/login');
        return;
      }
      setError(`Failed to load configuration: ${err.message}`);
    } finally {
      setConfigLoading(false);
    }
  }, [state.config, dispatch, logout, navigate]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSearch = (e) => {
    e.preventDefault();

    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);

    if (endDate <= startDate) {
      setError('Check-out date must be after check-in date.');
      return;
    }

    dispatch({
      type: 'SET_DATES',
      checkIn: startDate,
      checkOut: endDate,
    });

    navigate('/availability');
  };

  const nights = (() => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  })();

  return (
    <div className="search-landing">
      <section className="search-hero">
        <img
          src={HERO_IMAGE}
          alt="Sunset over the fjord at Kirsebærkroen"
          className="search-hero__img"
        />
        <div className="search-hero__overlay" />

        <div className="search-hero__panel-wrap">
          <div className="search-hero__panel">
            {/* Logos */}
            <div className="search-hero__logos">
              <img
                src="/images/logo-light.png"
                alt="Kirsebærkroen"
                className="search-hero__logo-main"
              />
              <div className="search-hero__logo-divider" />
              <img
                src="/images/nordic-logo.webp"
                alt="Nordic"
                className="search-hero__logo-partner"
              />
            </div>

            {/* Title */}
            <h1 className="search-hero__title">
              Nordic Agent Booking Portal
            </h1>
            <p className="search-hero__subtitle">
              Kirseb&aelig;rkroen &mdash; The Inn Between
            </p>

            {state.config && (
              <span className="search-hero__badge">
                Connected to {state.config.enterprise?.name || 'Mews'}
              </span>
            )}

            {configLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-brand-gold border-t-transparent" />
                <p className="mt-3 text-white/60 text-sm">Loading configuration&hellip;</p>
              </div>
            ) : (
              <form onSubmit={handleSearch} className="search-hero__form">
                <div className="search-hero__field">
                  <label htmlFor="checkIn">Arrival</label>
                  <input
                    id="checkIn"
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    required
                  />
                </div>

                <div className="search-hero__field">
                  <label htmlFor="checkOut">Departure</label>
                  <input
                    id="checkOut"
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    required
                  />
                </div>

                {nights > 0 && (
                  <p className="search-hero__nights">
                    {nights} night{nights !== 1 ? 's' : ''}
                  </p>
                )}

                <button type="submit" className="search-hero__cta">
                  Search Availability
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/calendar')}
                  className="mt-3 w-full text-center text-sm text-white/70 hover:text-white underline underline-offset-4 transition-colors"
                >
                  Or browse the availability calendar
                </button>
              </form>
            )}

            {error && (
              <div className="mt-4">
                <Toast message={error} type="error" dismissError={() => setError('')} />
              </div>
            )}
          </div>

          <p className="search-hero__footer">
            nordic.be &middot; Travel Agency Portal
          </p>
        </div>
      </section>
      {/* Email Import Section */}
      <section className="max-w-2xl mx-auto px-4 md:px-6 py-10">
        <div className="flex items-center gap-3 mb-1">
          <h2 className="text-xl font-medium text-brand-dark">Import from Email</h2>
          <span className="text-xs font-medium tracking-wide uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            Quick Import
          </span>
        </div>
        <p className="text-sm text-brand-muted mb-4">
          Paste a Nordic booking confirmation email to generate a prefilled booking link.
        </p>

        <textarea
          value={emailText}
          onChange={(e) => { setEmailText(e.target.value); setEmailResult(null); }}
          placeholder="Paste the full booking email text here..."
          rows={6}
          className="input-field font-mono text-xs w-full mb-3"
        />

        <button
          onClick={async () => {
            if (!emailText.trim()) {
              setError('Paste an email first');
              return;
            }
            try {
              setEmailParsing(true);
              setError('');
              setEmailResult(null);
              const result = await parseEmail(emailText);
              setEmailResult(result);
            } catch (err) {
              setError(err.message);
            } finally {
              setEmailParsing(false);
            }
          }}
          disabled={emailParsing || !emailText.trim()}
          className={`btn-gold ${emailParsing || !emailText.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {emailParsing ? 'Parsing...' : 'Parse & Generate Link'}
        </button>

        {emailResult && (
          <div className="mt-6 card p-5">
            <h3 className="text-sm font-medium text-brand-dark mb-3">Parsed Result</h3>

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
              <div>
                <span className="text-brand-muted">PO:</span>{' '}
                <span className="font-mono">{emailResult.parsedData.poReference || '—'}</span>
              </div>
              <div>
                <span className="text-brand-muted">Dates:</span>{' '}
                <span className="font-mono">{emailResult.parsedData.checkIn} &rarr; {emailResult.parsedData.checkOut}</span>
              </div>
              <div>
                <span className="text-brand-muted">Guest:</span>{' '}
                {emailResult.parsedData.mainGuest?.firstName} {emailResult.parsedData.mainGuest?.lastName}
              </div>
              <div>
                <span className="text-brand-muted">Rooms:</span>{' '}
                {emailResult.parsedData.roomLines?.map((rl) => `${rl.quantity}x ${rl.roomTypeRaw || rl.roomType}`).join(', ') || '—'}
              </div>
              <div>
                <span className="text-brand-muted">Guests:</span>{' '}
                {emailResult.parsedData.guests?.length || 0}
              </div>
              <div>
                <span className="text-brand-muted">Total:</span>{' '}
                <span className="font-mono">{emailResult.parsedData.totalPriceDKK || 0} DKK</span>
              </div>
            </div>

            <div className="bg-gray-50 border border-brand-border rounded-lg p-4">
              <label className="block text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
                Prefilled Booking Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={emailResult.prefillUrl}
                  readOnly
                  className="input-field font-mono text-xs flex-1"
                  onClick={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(emailResult.prefillUrl)}
                  className="btn-outline text-xs whitespace-nowrap"
                >
                  Copy
                </button>
              </div>
              <a
                href={emailResult.prefillUrl}
                className="inline-block mt-2 text-xs text-brand-gold hover:text-brand-gold-dark transition-colors"
              >
                Open link &rarr;
              </a>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Search;
